import {z} from 'zod';
import {WebLangModule} from '@brunata-angular/util';
import {OptionItem} from '../ui/option-card/option-card.model';

export const headerRowModes = ['EVERY_EXPORT', 'FIRST_EXPORT_ONLY', 'NONE'] as const;

export const headerRowModeSchema = z.enum(headerRowModes);

export type HeaderRowMode = z.infer<typeof headerRowModeSchema>;

export const unitPlacements = ['OWN_COLUMN', 'FOLDED_INTO_VALUE'] as const;

export const unitPlacementSchema = z.enum(unitPlacements);

export type UnitPlacement = z.infer<typeof unitPlacementSchema>;

export const headerRowOptions: ReadonlyArray<OptionItem & {id: HeaderRowMode}> = [
    {
        id: 'EVERY_EXPORT',
        titleKey: 'customizedexports_export_columns_header_every_title',
        descriptionKey: 'customizedexports_export_columns_header_every_desc',
    },
    {
        id: 'FIRST_EXPORT_ONLY',
        titleKey: 'customizedexports_export_columns_header_first_title',
        descriptionKey: 'customizedexports_export_columns_header_first_desc',
    },
    {
        id: 'NONE',
        titleKey: 'customizedexports_export_columns_header_none_title',
        descriptionKey: 'customizedexports_export_columns_header_none_desc',
    },
];

export const unitPlacementOptions: ReadonlyArray<OptionItem & {id: UnitPlacement}> = [
    {
        id: 'OWN_COLUMN',
        titleKey: 'customizedexports_export_columns_unit_own_title',
        descriptionKey: 'customizedexports_export_columns_unit_own_desc',
    },
    {
        id: 'FOLDED_INTO_VALUE',
        titleKey: 'customizedexports_export_columns_unit_folded_title',
        descriptionKey: 'customizedexports_export_columns_unit_folded_desc',
    },
];

export const exportColumnIds = [
    'systemNo',
    'serialNo',
    'printedSerialNo',
    'propertyNo',
    'branchNo',
    'locationNo',
    'street',
    'stairway',
    'floor',
    'placement',
    'usage',
    'type',
    'latestReading',
    'meterValue',
    'unit',
    'installationDate',
    'dismountedDate',
] as const;

export const exportColumnIdSchema = z.enum(exportColumnIds);

export type ExportColumnId = z.infer<typeof exportColumnIdSchema>;

// Same keys the online-webservice writes into the file.
export const exportColumnHeadingBundle: WebLangModule = {
    application: 'webbill',
    module: 'onlinewebservice',
};

type ExportColumnField = {labelKey: string; headingKey: string};

export const exportColumnFields: Record<ExportColumnId, ExportColumnField> = {
    systemNo: {
        labelKey: 'customizedexports_export_column_system_no',
        headingKey: 'report_buildingno',
    },
    serialNo: {
        labelKey: 'customizedexports_export_column_serial_no',
        headingKey: 'report_serialno',
    },
    printedSerialNo: {
        labelKey: 'customizedexports_export_column_printed_serial_no',
        headingKey: 'report_printedserialno',
    },
    propertyNo: {
        labelKey: 'customizedexports_export_column_property_no',
        headingKey: 'report_propertyno',
    },
    branchNo: {
        labelKey: 'customizedexports_export_column_branch_no',
        headingKey: 'report_branchno',
    },
    locationNo: {
        labelKey: 'customizedexports_export_column_location_no',
        headingKey: 'report_locationno',
    },
    street: {labelKey: 'customizedexports_export_column_street', headingKey: 'report_street'},
    stairway: {labelKey: 'customizedexports_export_column_stairway', headingKey: 'report_stairway'},
    floor: {labelKey: 'customizedexports_export_column_floor', headingKey: 'report_floor'},
    placement: {
        labelKey: 'customizedexports_export_column_placement',
        headingKey: 'report_placement',
    },
    usage: {labelKey: 'customizedexports_export_column_usage', headingKey: 'report_usage'},
    type: {labelKey: 'customizedexports_export_column_type', headingKey: 'report_type'},
    latestReading: {
        labelKey: 'customizedexports_export_column_latest_reading',
        headingKey: 'report_latestreading',
    },
    meterValue: {
        labelKey: 'customizedexports_export_column_meter_value',
        headingKey: 'report_metervalue',
    },
    unit: {labelKey: 'customizedexports_export_column_unit', headingKey: 'report_unit'},
    installationDate: {
        labelKey: 'customizedexports_export_column_installation_date',
        headingKey: 'report_installationdate',
    },
    dismountedDate: {
        labelKey: 'customizedexports_export_column_dismounted',
        headingKey: 'report_dismounteddate',
    },
};

// Only in the file when the Meters & channels step keeps dismounted meters (AGI-18671).
export const dismountedColumnId: ExportColumnId = 'dismountedDate';

export const exportColumnStateSchema = z.object({
    id: exportColumnIdSchema,
    included: z.boolean(),
    header: z.string().nullable(),
});

export type ExportColumnState = z.infer<typeof exportColumnStateSchema>;

export type OrderedExportColumn = ExportColumnState & {
    orderNumber: number | null;
    locked: boolean;
    labelKey: string;
    headingKey: string;
};

export const meterValueAndUnitLabelKey = 'customizedexports_export_column_meter_value_and_unit';

export function exportColumnHeadingKey(id: ExportColumnId, unitPlacement: UnitPlacement): string {
    return id === 'meterValue' && unitPlacement === 'FOLDED_INTO_VALUE'
        ? 'report_metervalueandunit'
        : exportColumnFields[id].headingKey;
}

export const defaultExportColumns: ReadonlyArray<ExportColumnState> = exportColumnIds.map(id => ({
    id,
    included: true,
    header: null,
}));

export function reconcileExportColumns(
    stored: ReadonlyArray<ExportColumnState>,
): ExportColumnState[] {
    const seen = new Set<ExportColumnId>();
    const reconciled: ExportColumnState[] = [];
    for (const column of stored) {
        if (!seen.has(column.id)) {
            seen.add(column.id);
            reconciled.push(column);
        }
    }
    for (const column of defaultExportColumns) {
        if (!seen.has(column.id)) {
            reconciled.splice(canonicalPlaceFor(column.id, reconciled), 0, column);
        }
    }
    return reconciled;
}

function canonicalPlaceFor(
    id: ExportColumnId,
    reconciled: ReadonlyArray<ExportColumnState>,
): number {
    const canonical = exportColumnIds.indexOf(id);
    const canonicalIndexOf = (column: ExportColumnState): number =>
        exportColumnIds.indexOf(column.id);
    const firstPredecessor = reconciled.findIndex(column => canonicalIndexOf(column) < canonical);
    const follower = reconciled.findIndex(
        (column, index) => index > firstPredecessor && canonicalIndexOf(column) > canonical,
    );
    return follower === -1 ? reconciled.length : follower;
}
